import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors, typography } from '../theme/tokens';
import Button from '../components/Button';
import { Key, Upload } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getCloudinaryConfig } from '../config/cloudinary';
import { generateSignature } from '../utils/cloudinary';

const SettingsContainer = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const SettingsHeader = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TitleSection = styled.div``;

const SettingsTitle = styled(TitleLarge)`
  color: ${colors.neutral.black};
  margin-bottom: 1rem;
`;

const SettingsSubtitle = styled(SubtitleMedium)`
  color: ${colors.neutral.darkCharcoal};
`;

const ContentContainer = styled.div`
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const SettingsForm = styled.div`
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${colors.brand.darkLaurel};
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  margin-bottom: 1rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  padding-bottom: 0.5rem;
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: ${colors.neutral.offWhite};
  border-radius: 4px;
`;

const Label = styled.label`
  min-width: 120px;
  font-family: 'Gabarito', sans-serif;
  font-weight: 500;
  color: ${colors.neutral.darkCharcoal};
  font-size: 14px;
`;

const Value = styled.div`
  flex: 1;
  font-family: 'Gabarito', sans-serif;
  color: ${colors.neutral.darkCharcoal};
  font-size: 14px;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${colors.neutral.lightCharcoal};
`;

const ColorInput = styled.input`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: none;
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;
  color: ${colors.neutral.darkCharcoal};
  background-color: ${colors.neutral.white};

  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
  }

  &:disabled {
    background-color: ${colors.neutral.offWhite};
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
`;

const ImagePreviewContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px dashed ${colors.neutral.lightCharcoal};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: ${colors.brand.laurel};
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadErrorMessage = styled.div`
  color: ${colors.reds.bright};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const ResetPasswordLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.brand.laurel};
  text-decoration: none;
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: ${colors.neutral.offWhite};
    color: ${colors.brand.darkLaurel};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.darkCharcoal};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.reds.bright};
`;

interface CreatorDetails {
  id: number;
  FirstName: string;
  LastName: string;
  ProfilePicture: string;
  email: string;
  accent_color: string;
}

const Settings = () => {
  const [creatorDetails, setCreatorDetails] = useState<CreatorDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState<CreatorDetails | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCreatorDetails = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('CreatorDetails')
          .select('id, FirstName, LastName, ProfilePicture, email, accent_color')
          .eq('email', user.email)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        setCreatorDetails(data);
        setEditedDetails(data);
      } catch (err) {
        console.error('Error fetching creator details:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    fetchCreatorDetails();
  }, [user]);

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (editedDetails) {
      setEditedDetails({
        ...editedDetails,
        accent_color: event.target.value
      });
    }
  };

  const handleFieldChange = (field: keyof CreatorDetails, value: string) => {
    if (editedDetails) {
      setEditedDetails({
        ...editedDetails,
        [field]: value
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedDetails(creatorDetails);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    try {
      const timestamp = Math.round(new Date().getTime() / 1000);
      const signature = await generateSignature(timestamp);
      const cloudinaryConfig = getCloudinaryConfig();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', cloudinaryConfig.apiKey);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      setEditedDetails(prev => prev ? {
        ...prev,
        ProfilePicture: data.secure_url
      } : null);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
      setSelectedImage(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!editedDetails || !user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('CreatorDetails')
        .update({
          FirstName: editedDetails.FirstName,
          LastName: editedDetails.LastName,
          ProfilePicture: editedDetails.ProfilePicture,
          accent_color: editedDetails.accent_color
        })
        .eq('id', editedDetails.id);

      if (error) throw error;

      setCreatorDetails(editedDetails);
      setIsEditing(false);
      setSelectedImage(null);
      setUploadError(null);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SettingsContainer>
        <LoadingMessage>Loading settings...</LoadingMessage>
      </SettingsContainer>
    );
  }

  if (error) {
    return (
      <SettingsContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </SettingsContainer>
    );
  }

  if (!creatorDetails) {
    return (
      <SettingsContainer>
        <ErrorMessage>No settings found</ErrorMessage>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <SettingsHeader>
        <HeaderContent>
          <TitleSection>
            <SettingsTitle>Settings</SettingsTitle>
            <SettingsSubtitle>Manage your account settings and preferences</SettingsSubtitle>
          </TitleSection>
          <ActionButtons>
            {!isEditing ? (
              <Button onClick={handleEdit}>
                Edit
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </ActionButtons>
        </HeaderContent>
      </SettingsHeader>

      <ContentContainer>
        <SettingsForm>
          <FormSection>
            <SectionTitle>Profile Information</SectionTitle>
            
            <FormRow>
              <Label>ID:</Label>
              <Value>{creatorDetails.id}</Value>
            </FormRow>

            <FormRow>
              <Label>First Name:</Label>
              <Value>
                {isEditing ? (
                  <Input
                    type="text"
                    value={editedDetails?.FirstName || ''}
                    onChange={(e) => handleFieldChange('FirstName', e.target.value)}
                  />
                ) : (
                  creatorDetails.FirstName
                )}
              </Value>
            </FormRow>

            <FormRow>
              <Label>Last Name:</Label>
              <Value>
                {isEditing ? (
                  <Input
                    type="text"
                    value={editedDetails?.LastName || ''}
                    onChange={(e) => handleFieldChange('LastName', e.target.value)}
                  />
                ) : (
                  creatorDetails.LastName
                )}
              </Value>
            </FormRow>

            <FormRow>
              <Label>Email:</Label>
              <Value>
                {isEditing ? (
                  <Input
                    type="email"
                    value={editedDetails?.email || ''}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                  />
                ) : (
                  creatorDetails.email
                )}
              </Value>
            </FormRow>

            <FormRow>
              <Label>Profile Picture:</Label>
              <Value>
                {isEditing ? (
                  <ImageUploadContainer>
                    <ImagePreviewContainer onClick={handleImageClick}>
                      {selectedImage ? (
                        <ImagePreview src={selectedImage} alt="Profile preview" />
                      ) : editedDetails?.ProfilePicture ? (
                        <ImagePreview 
                          src={editedDetails.ProfilePicture} 
                          alt="Profile" 
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/80x80?text=No+Image';
                          }}
                        />
                      ) : (
                        <ImagePreview 
                          src="https://via.placeholder.com/80x80?text=No+Image" 
                          alt="No Profile Picture" 
                        />
                      )}
                    </ImagePreviewContainer>
                    <UploadButton 
                      variant="outline" 
                      onClick={handleImageClick} 
                      disabled={isUploading}
                    >
                      <Upload size={16} />
                      {isUploading ? 'Uploading...' : 'Upload Profile Picture'}
                    </UploadButton>
                    <HiddenFileInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {uploadError && <UploadErrorMessage>{uploadError}</UploadErrorMessage>}
                  </ImageUploadContainer>
                ) : (
                  creatorDetails.ProfilePicture ? (
                    <ProfileImage 
                      src={creatorDetails.ProfilePicture} 
                      alt="Profile" 
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/80x80?text=No+Image';
                      }}
                    />
                  ) : (
                    <ProfileImage 
                      src="https://via.placeholder.com/80x80?text=No+Image" 
                      alt="No Profile Picture" 
                    />
                  )
                )}
              </Value>
            </FormRow>

            <FormRow>
              <Label>Accent Color:</Label>
              <Value>
                <ColorInput
                  type="color"
                  value={editedDetails?.accent_color || '#4A6741'}
                  onChange={handleColorChange}
                  title="Choose accent color"
                  disabled={!isEditing}
                />
                <span style={{ marginLeft: '0.5rem', color: colors.neutral.darkCharcoal }}>
                  {editedDetails?.accent_color || '#4A6741'}
                </span>
              </Value>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle>Account Security</SectionTitle>
            
            <FormRow>
              <Label>Password:</Label>
              <Value>
                <ResetPasswordLink onClick={() => navigate('/change-password')}>
                  <Key size={16} />
                  Change Password
                </ResetPasswordLink>
              </Value>
            </FormRow>
          </FormSection>
        </SettingsForm>
      </ContentContainer>
    </SettingsContainer>
  );
};

export default Settings; 